import { useMutation } from '@apollo/client';
import {
  faBan,
  faInfoCircle,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { DiagramEditorPartsFragment } from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import { ListGroup } from '~/components/common/ListGroup';
import { Panel } from '~/components/common/Panel';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { DiagramAreaFormFields } from '~/components/upload/diagram/diagram-editor/diagram-area-form-fields';
import { DiagramPathDrawingField } from '~/components/upload/diagram/diagram-editor/path-drawing-field';
import { cn } from '~/utils/common';
import { useZodForm } from '~/utils/forms';
import {
  diagramAreaSchema,
  initialDiagramArea,
  linkTarget,
} from '~/utils/modules/diagram';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const diagramEditorParts = graphql(`
  fragment diagramEditorParts on Diagram {
    ...diagramParts
    file {
      ...fileParts
    }
    diagramAreas {
      ...diagramAreaParts
    }
  }
`);

export const ADD_DIAGRAM_AREA = graphql(`
  mutation AddDiagramArea($id: ID!, $input: DiagramAddDiagramAreaInput!) {
    diagramAddDiagramArea(id: $id, input: $input) {
      result {
        ...diagramEditorParts
      }
    }
  }
`);

export const DELETE_DIAGRAM_AREA = graphql(`
  mutation RemoveDiagramArea($id: ID!, $input: DiagramDeleteDiagramAreaInput!) {
    diagramDeleteDiagramArea(id: $id, input: $input) {
      result {
        ...diagramEditorParts
      }
    }
  }
`);

type Props = {
  diagram: DiagramEditorPartsFragment;
};

export function DiagramEditor(props: Props) {
  return (
    <ApolloProviderV4>
      <DiagramEditorInner {...props} />
    </ApolloProviderV4>
  );
}

function DiagramEditorInner({ diagram }: Props) {
  const [hoveredAreaId, setHoveredAreaId] = useState<string | null>(null);

  const [isDrawing, setIsDrawing] = useState(false);

  const [addArea, { loading: loadingAddArea, error: errorAddArea }] =
    useMutation(ADD_DIAGRAM_AREA);

  const [removeArea, { loading: loadingRemoveArea }] =
    useMutation(DELETE_DIAGRAM_AREA);

  const form = useZodForm({
    schema: diagramAreaSchema,
    values: initialDiagramArea(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await addArea({
        variables: {
          id: diagram.id,
          input: { diagramArea: values },
        },
      });
      toast.success('Area saved successfully.');
      setIsDrawing(false);
      form.reset();
    } catch (err) {
      console.log('Error saving area', err);
      toast.error('There was a problem saving the area.');
    }
  });

  const handleDelete = (areaId: string) => async () => {
    const confirmMessage = 'The selected area will be deleted.';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await removeArea({
        variables: {
          id: diagram.id,
          input: { diagramAreaId: areaId },
        },
      });
      toast.success('Area deleted successfully.');
    } catch (err) {
      console.log('Error deleting area', err);
      toast.error('There was a problem deleting the area.');
    }
  };

  const handleAreaHovered = (areaId: string | null) => () => {
    setHoveredAreaId(areaId);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between items-end">
              <Heading level={3}>Image Areas</Heading>
              <div>
                {!isDrawing && (
                  <Button
                    type="button"
                    color="primary"
                    size="xs"
                    onClick={() => {
                      setIsDrawing(true);
                    }}
                    className="gap-1"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </Button>
                )}
                {isDrawing && (
                  <Button
                    type="button"
                    color="ghost"
                    size="xs"
                    className="gap-1"
                    onClick={() => {
                      setIsDrawing(false);
                      form.reset();
                    }}
                  >
                    <FontAwesomeIcon icon={faBan} /> Cancel
                  </Button>
                )}
              </div>
            </div>

            {!diagram.diagramAreas?.length && (
              <Alert>
                <FontAwesomeIcon icon={faInfoCircle} />
                <div>
                  No areas have been added yet. Use the <em>Add</em> button to
                  get started.
                </div>
              </Alert>
            )}

            {isDrawing && (
              <Panel>
                <Panel.Heading>
                  <Panel.Title>New Area</Panel.Title>
                </Panel.Heading>
                <Panel.Body className="space-y-4">
                  <DiagramAreaFormFields />
                  <HookFormErrors graphQLError={errorAddArea} />
                </Panel.Body>
                <Panel.Footer className="text-right">
                  <Button
                    type="submit"
                    color="primary"
                    loading={loadingAddArea}
                  >
                    Save Area
                  </Button>
                </Panel.Footer>
              </Panel>
            )}

            <ListGroup border={false}>
              {diagram.diagramAreas?.map(area => (
                <ListGroup.Item
                  key={area.id}
                  onMouseOver={handleAreaHovered(area.id)}
                  onMouseOut={handleAreaHovered(null)}
                  className={cn('border-l-4 border-slate-200 p-4 my-2', {
                    'border-l-secondary': area.id === hoveredAreaId,
                  })}
                >
                  <div className="flex justify-between items-center">
                    {area.label}
                    <div className="text-muted text-sm break-words">
                      {linkTarget(area.linkTargetType, area.linkTargetId)}
                    </div>

                    <Button
                      type="button"
                      color="ghost"
                      size="xs"
                      onClick={handleDelete(area.id)}
                      disabled={loadingRemoveArea}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>

          <div className="lg:col-span-2">
            {diagram.file && (
              <DiagramPathDrawingField
                areas={diagram.diagramAreas ?? []}
                image={diagram.file.signedUrl}
                hoveredAreaId={hoveredAreaId}
                onHover={handleAreaHovered}
                isDrawing={isDrawing}
              />
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
